import { QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import useHandleAppException, {
  AppExceptionResponseData,
} from '../../../hooks/useHandleAppException';
import LangEnum from '../../../lang/LangEnum';
import { useApplicationContext } from '../../../modules/context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../../modules/translation/TranslationContext';
import snackbarStore from '../../../stores/Snackbar.store';
import { getGetCatalogSectionsKey } from '../catalogs/catalogs.keys';
import { applyOptimisticMove, rollbackMove } from './catalogDragAndDropCache.utils';
import { getCatalogProductsPageKeyPrefix } from './catalogProducts.keys';
import { moveCatalogItem } from './catalogProducts.repository';
import {
  MoveCatalogItemMutationInput,
  MoveCatalogItemOptimisticParams,
} from './catalogProducts.types';

interface BuildCatalogMoveKeysParams {
  teamId?: string;
  catalogId: string;
  optimisticParams: MoveCatalogItemOptimisticParams;
}

const buildCatalogMoveKeys = ({
  teamId,
  catalogId,
  optimisticParams,
}: BuildCatalogMoveKeysParams): { pageKeyPrefix: QueryKey; sectionsKey: QueryKey } => {
  const { contextParams, sortParams, groupParams, filters, customerIds, gridDataInclude } =
    optimisticParams;

  const pageKeyPrefix: QueryKey = [
    teamId,
    ...getCatalogProductsPageKeyPrefix({
      catalogId,
      contextParams,
      sortParams,
      groupParams,
      filters,
      customerIds,
      gridDataInclude,
    }),
  ];
  const sectionsKey: QueryKey = [
    teamId,
    ...getGetCatalogSectionsKey({
      id: catalogId,
      filters,
      customerIds,
      contextParams,
      groupParams,
    }),
  ];

  return { pageKeyPrefix, sectionsKey };
};

export const useMoveCatalogItem = () => {
  const { team } = useApplicationContext();
  const queryClient = useQueryClient();
  const { translate } = useTranslation();
  const { handleError } = useHandleAppException();

  return useMutation({
    onMutate: async (input: MoveCatalogItemMutationInput) => {
      const { catalogId, fromSectionId, sectionProductId, toSectionId, afterItemId } = input;
      const { contextParams, sortParams, groupParams, filters, customerIds, gridDataInclude } =
        input.optimisticParams;

      const { pageKeyPrefix, sectionsKey } = buildCatalogMoveKeys({
        teamId: team?.id,
        catalogId,
        optimisticParams: input.optimisticParams,
      });

      await Promise.all([
        queryClient.cancelQueries({ queryKey: pageKeyPrefix }),
        queryClient.cancelQueries({ queryKey: sectionsKey }),
      ]);

      const { snapshot } = applyOptimisticMove({
        queryClient,
        teamId: team?.id,
        catalogId,
        contextParams,
        sortParams,
        groupParams,
        filters,
        customerIds,
        gridDataInclude,
        movedItemId: sectionProductId,
        fromSectionId,
        toSectionId,
        afterItemId,
      });

      return { snapshot };
    },
    mutationFn: async (input: MoveCatalogItemMutationInput) => {
      const response = await moveCatalogItem({
        catalogId: input.catalogId,
        sectionId: input.fromSectionId,
        sectionProductId: input.sectionProductId,
        toSectionId: input.toSectionId,
        afterItemId: input.afterItemId,
      });

      if (!response.ok) {
        throw response.originalError;
      }

      return response.data;
    },
    onError: (error, _input, context) => {
      if (context?.snapshot) {
        rollbackMove(context.snapshot);
      }

      const errorData = (error as AxiosError<AppExceptionResponseData>).response?.data;
      const message = errorData ? handleError(errorData) : undefined;
      snackbarStore.handleMessage({
        type: 'error',
        message: message || translate(LangEnum.GLOBAL_ERROR_MESSAGE),
      });
    },
    onSettled: (_data, _error, input) => {
      const { pageKeyPrefix, sectionsKey } = buildCatalogMoveKeys({
        teamId: team?.id,
        catalogId: input.catalogId,
        optimisticParams: input.optimisticParams,
      });

      queryClient.invalidateQueries({ queryKey: pageKeyPrefix.slice(0, -1) });
      queryClient.invalidateQueries({ queryKey: sectionsKey });
    },
  });
};
